import API_BASE_URL from 'src/constants';
import { MaintenanceWindow, isValidMaintenanceWindow } from 'src/services/maintenanceState';

/**
 * Public maintenance status endpoint (`GET /portal/maintenance`).
 *
 * Plain `fetch` on purpose: the endpoint needs no token (the login screen asks
 * it), and `src/generated/v1.d.ts` does not know the route until the backend
 * is on the dev server. Once `yarn generate:api-code` includes it, switch to
 * `$publicApi.useQuery('get', '/maintenance')` and drop `parseMaintenanceStatus`.
 *
 * Classification matters more than the payload here, because the hook treats
 * a thrown `MaintenanceUnreachableError` as "backend down, keep the stored
 * window" and a resolved value as "backend answered":
 *  - network error, timeout, HTTP >= 500, or a 2xx that is not a status JSON
 *    (a proxy or maintenance page) -> `MaintenanceUnreachableError`
 *  - other HTTP errors (a 404 while the backend does not ship the route yet)
 *    -> reachable, no window
 */
export interface MaintenanceStatus {
  window: MaintenanceWindow | null;
  /** Server clock (ISO 8601), `null` when the answer carried none. */
  serverTime: string | null;
}

export class MaintenanceUnreachableError extends Error {
  readonly reason?: unknown;

  constructor(message: string, reason?: unknown) {
    super(message);
    this.name = 'MaintenanceUnreachableError';
    this.reason = reason;
  }
}

export const MAINTENANCE_FETCH_TIMEOUT_MS = 8000;

export const maintenanceEndpointUrl = (): string => `${API_BASE_URL}portal/maintenance`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isoOrNull = (value: unknown): string | null =>
  typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null;

/** Maps the backend body to a status; `null` when the body is not a status object at all. */
export const parseMaintenanceStatus = (body: unknown): MaintenanceStatus | null => {
  if (!isRecord(body)) return null;

  const start = isoOrNull(body.start);
  const end = isoOrNull(body.end);
  const message = typeof body.message === 'string' && body.message.trim() ? body.message.trim() : null;
  const candidate = start && end ? { start, end, message } : null;

  return {
    window: isValidMaintenanceWindow(candidate) ? candidate : null,
    serverTime: isoOrNull(body.server_time),
  };
};

export const fetchMaintenanceStatus = async (signal?: AbortSignal): Promise<MaintenanceStatus> => {
  // No `AbortSignal.any` (missing in jsdom): own controller, fed by the timeout
  // and by the caller's signal.
  const controller = new AbortController();
  const forwardAbort = () => controller.abort();
  const timer = setTimeout(forwardAbort, MAINTENANCE_FETCH_TIMEOUT_MS);
  signal?.addEventListener('abort', forwardAbort);
  if (signal?.aborted) controller.abort();

  try {
    let response: Response;
    try {
      response = await fetch(maintenanceEndpointUrl(), { cache: 'no-store', signal: controller.signal });
    } catch (error) {
      throw new MaintenanceUnreachableError('Maintenance status request failed', error);
    }

    if (response.status >= 500) {
      throw new MaintenanceUnreachableError(`Maintenance status answered ${response.status}`);
    }
    if (!response.ok) return { window: null, serverTime: null };

    let body: unknown;
    try {
      body = await response.json();
    } catch (error) {
      throw new MaintenanceUnreachableError('Maintenance status body is not JSON', error);
    }

    const status = parseMaintenanceStatus(body);
    if (!status) throw new MaintenanceUnreachableError('Maintenance status body has an unexpected shape');
    return status;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', forwardAbort);
  }
};
